import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchFeedItems, performItemAction } from '../../api/products';
import { useFeed } from '../../contexts/FeedContext';
import FeedCard from '../../components/FeedCard';
import { clearStartappItemRoot } from '../../utils/startappItemEntry';
import { track } from '../../utils/productAnalytics';
import './FeedPage.css';

const PAGE_SIZE = 20;
const STACK_SIZE = 10; /* фиксированный размер стека, как в Lunch-mate */
const TAP_MAX_DURATION_MS = 320;
const TAP_MOVE_TOLERANCE_PX = 12;
/** Минимальный горизонтальный сдвиг для фиксации оси жеста */
const AXIS_LOCK_PX = 10;
/** Сдвиг по горизонтали, при котором жест считается свайпом (с любой точки экрана) */
const SHORT_SWIPE_COMMIT_PX = 40;
/** Доминирование оси: горизонталь vs вертикаль */
const AXIS_DOMINANCE_RATIO = 1.22;
const EXIT_ANIMATION_MS = 400;
/** Удержание без движения — подсказки по краям (красный / голубой) */
const HOLD_HINT_MS = 480;
const HOLD_CANCEL_MOVE_PX = 14;

export default function FeedPage() {
  const navigate = useNavigate();
  const {
    items,
    setItems,
    loading,
    setLoading,
    error,
    setError,
    currentIndex,
    setCurrentIndex,
    currentPhotoIndex,
    setCurrentPhotoIndex,
    hasMore,
    setHasMore,
    nextOffset,
    setNextOffset,
    itemLikedFromProductPage,
    setItemLikedFromProductPage,
  } = useFeed();

  /* Логика тапов/свайпов как в Lunch-mate */
  const [currentZone, setCurrentZone] = useState('center');
  const [rotationClass, setRotationClass] = useState('');
  const [interactionMode, setInteractionMode] = useState(null); // 'tap' | 'swipe' | null
  const [isSwipeMode, setIsSwipeMode] = useState(false);
  const [exitAnimation, setExitAnimation] = useState(null); // 'left' | 'right' | null — улетание карточки
  const [showEndOfFeed, setShowEndOfFeed] = useState(false);
  const [showEdgeHints, setShowEdgeHints] = useState(false);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const maxDx = useRef(0);
  const maxDy = useRef(0);
  const isTracking = useRef(false);
  const holdTimerRef = useRef(null);
  /** null — не решили; 'h' — горизонталь (свайп лайк/дизлайк); 'v' — вертикаль (не перехватываем как свайп) */
  const gestureAxisRef = useRef(null);
  /** Горизонтальный свайп зафиксирован по порогу смещения */
  const swipeArmedRef = useRef(false);
  /** Зона тапа в момент начала жеста — читаем в конце, т.к. state к тому моменту может не обновиться */
  const touchZoneRef = useRef('center');
  /** Rect контента ленты на pointerdown — трети ширины по видимой области, а не по window (иначе зоны «плывут») */
  const gestureContentRectRef = useRef(null);
  /** Pointer Events: один активный указатель (мышь / тач / перо) */
  const activePointerIdRef = useRef(null);
  /** Не запускать второй подряд loadMore(append), пока первый не завершится (как в Lunch-mate с preload) */
  const isLoadingMoreRef = useRef(false);
  /**
   * Текущие карточки → query seen. Только RAM: после закрытия приложения seen пустой,
   * сервер снова может отдать ранее показанные вещи (кроме лайк/дизлайк в БД).
   */
  const itemsRef = useRef([]);
  itemsRef.current = items;

  const feedActiveRequestIdRef = useRef(null);
  const feedCardShownAtRef = useRef(null);
  const feedLastItemIdRef = useRef(null);
  const feedSessionCardSeqRef = useRef(0);

  const loadMore = useCallback(async (append = true, options = {}) => {
    if (append && isLoadingMoreRef.current) return;
    if (append) isLoadingMoreRef.current = true;
    const { advanceToFirstNewIndex, onNoMore } = options;
    const requestId =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `f${Date.now()}`;
    feedActiveRequestIdRef.current = requestId;
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      if (!append) setLoading(true);
      setError(null);
      const seenIds =
        append && itemsRef.current.length > 0 ? itemsRef.current.map((x) => x.id) : null;
      const data = await fetchFeedItems(PAGE_SIZE, seenIds);
      const list = Array.isArray(data?.items) ? data.items : [];
      let appendedCount = 0;
      setItems((prev) => {
        if (!append) return list;
        const existing = new Set(prev.map((x) => x.id));
        const fresh = list.filter((x) => x && !existing.has(x.id));
        appendedCount = fresh.length;
        return [...prev, ...fresh];
      });
      const serverMore = Boolean(data?.has_more);
      setHasMore(append && list.length > 0 && appendedCount === 0 ? false : serverMore);
      setNextOffset(null);
      if (!append && list.length > 0) {
        setCurrentIndex(0);
        setCurrentPhotoIndex(0);
      }
      if (append && advanceToFirstNewIndex != null) {
        if (appendedCount > 0) {
          setCurrentIndex(advanceToFirstNewIndex);
          setCurrentPhotoIndex(0);
        } else if (onNoMore) onNoMore();
      }
      const durationMs =
        typeof performance !== 'undefined'
          ? Math.round(performance.now() - t0)
          : Math.round(Date.now() - t0);
      track('feed_batch_loaded', {
        request_id: requestId,
        append,
        duration_ms: durationMs,
        items_count: list.length,
        has_more: serverMore,
        seen_len: Array.isArray(seenIds) ? seenIds.length : 0,
      });
    } catch (e) {
      track('feed_batch_error', {
        request_id: requestId,
        append,
        message: String(e?.message || e || 'error').slice(0, 200),
      });
      setError(e.message || 'Ошибка загрузки');
      setItems((prev) => (append ? prev : []));
      if (append && onNoMore) onNoMore();
    } finally {
      setLoading(false);
      if (append) isLoadingMoreRef.current = false;
    }
  }, [setItems, setLoading, setError, setHasMore, setNextOffset, setCurrentIndex, setCurrentPhotoIndex]);

  useEffect(() => {
    if (items.length === 0) loadMore(false);
  }, [items.length, loadMore]);

  /* При возврате в ленту: если с страницы товара поставили лайк — перейти к следующей карточке */
  useEffect(() => {
    if (items.length === 0 || itemLikedFromProductPage == null) return;
    const currentItem = items[currentIndex] ?? null;
    if (currentItem?.id === itemLikedFromProductPage) {
      setItemLikedFromProductPage(null);
      setCurrentIndex((i) => (i < items.length - 1 ? i + 1 : i));
      setCurrentPhotoIndex(0);
    }
  }, [itemLikedFromProductPage, currentIndex, items, setItemLikedFromProductPage, setCurrentIndex, setCurrentPhotoIndex]);

  /* Подгрузка следующей пачки карточек, когда до конца стопки остаётся мало */
  const LOAD_MORE_THRESHOLD = 5;
  useEffect(() => {
    if (!hasMore || items.length === 0) return;
    const remaining = items.length - currentIndex;
    if (remaining <= LOAD_MORE_THRESHOLD) {
      loadMore(true);
    }
  }, [currentIndex, items.length, hasMore, loadMore]);

  /* Алгоритм стопки как в Lunch-mate: фиксированный массив из STACK_SIZE слотов,
   * слот i = items[currentIndex + i] или null; пустые слоты — пустой div с display:none;
   * zIndex: 10 - index; активная карточка — слот 0 (pointer-events). */
  const stackSlots = [];
  for (let i = 0; i < STACK_SIZE; i++) {
    stackSlots.push(items[currentIndex + i] ?? null);
  }

  const currentItem = items[currentIndex] ?? null;

  const photoCount = Math.max(1, (currentItem?.photos && Array.isArray(currentItem.photos) ? currentItem.photos.filter((p) => p?.file_path).length : 0));

  useEffect(() => {
    const id = currentItem?.id;
    if (id == null) return undefined;
    const now = Date.now();
    const prevId = feedLastItemIdRef.current;
    const prevAt = feedCardShownAtRef.current;
    if (prevId != null && prevAt != null && prevId !== id) {
      track('feed_card_dwell_ms', { item_id: prevId, dwell_ms: now - prevAt });
    }
    feedLastItemIdRef.current = id;
    feedCardShownAtRef.current = now;
    feedSessionCardSeqRef.current += 1;
    const pc = Math.max(
      1,
      currentItem?.photos && Array.isArray(currentItem.photos)
        ? currentItem.photos.filter((p) => p?.file_path).length
        : 0
    );
    track('feed_card_impression', {
      item_id: id,
      stack_index: currentIndex,
      card_seq: feedSessionCardSeqRef.current,
      feed_request_id: feedActiveRequestIdRef.current,
      group_id: currentItem?.group_id ?? null,
      item_type_id: currentItem?.item_type_id ?? null,
      photo_count: pc,
    });
    return undefined;
  }, [currentItem?.id, currentIndex]);

  useEffect(
    () => () => {
      const prevId = feedLastItemIdRef.current;
      const prevAt = feedCardShownAtRef.current;
      if (prevId != null && prevAt != null) {
        track('feed_card_dwell_ms', { item_id: prevId, dwell_ms: Date.now() - prevAt });
      }
    },
    []
  );

  useEffect(() => {
    if (showEndOfFeed) track('feed_end_screen', {});
  }, [showEndOfFeed]);

  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [currentIndex, setCurrentPhotoIndex]);

  const goPrevCard = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const goNextCard = useCallback(() => {
    setCurrentIndex((i) => (i < items.length - 1 ? i + 1 : i));
  }, [items.length]);

  const prevPhoto = useCallback(() => {
    setCurrentPhotoIndex((i) => {
      const next = (i - 1 + photoCount) % photoCount;
      if (currentItem?.id) track('feed_photo_change', { item_id: currentItem.id, from_index: i, to_index: next });
      return next;
    });
  }, [photoCount, currentItem?.id]);

  const nextPhoto = useCallback(() => {
    setCurrentPhotoIndex((i) => {
      const next = (i + 1) % photoCount;
      if (currentItem?.id) track('feed_photo_change', { item_id: currentItem.id, from_index: i, to_index: next });
      return next;
    });
  }, [photoCount, currentItem?.id]);

  const openProduct = useCallback(
    (item) => {
      if (!item?.id) return;
      track('feed_open_product', { item_id: item.id });
      clearStartappItemRoot();
      navigate(`/main/catalog/${item.id}`, { state: { item, fromFeed: true } });
    },
    [navigate]
  );

  const getZoneByClientX = useCallback((clientX, rect) => {
    if (rect && rect.width > 0) {
      const rel = clientX - rect.left;
      const third = rect.width / 3;
      if (rel < third) return 'left';
      if (rel > 2 * third) return 'right';
      return 'center';
    }
    const w = typeof window !== 'undefined' ? window.innerWidth : 360;
    const third = w / 3;
    if (clientX < third) return 'left';
    if (clientX > 2 * third) return 'right';
    return 'center';
  }, []);

  const handleLike = useCallback(() => {
    if (!currentItem?.id || exitAnimation) return;
    const dwellMs =
      feedCardShownAtRef.current != null ? Date.now() - feedCardShownAtRef.current : null;
    track('feed_like', {
      item_id: currentItem.id,
      interaction: 'swipe',
      dwell_ms: dwellMs,
      photo_index: currentPhotoIndex,
    });
    const atLastCard = currentIndex === items.length - 1;
    const prevLength = items.length;
    setExitAnimation('right');
    performItemAction(currentItem.id, 'like').catch(() => {});
    setTimeout(() => {
      if (atLastCard) {
        loadMore(true, { advanceToFirstNewIndex: prevLength, onNoMore: () => setShowEndOfFeed(true) });
      } else {
        goNextCard();
      }
      setExitAnimation(null);
    }, EXIT_ANIMATION_MS);
  }, [currentItem, currentIndex, items.length, goNextCard, loadMore, exitAnimation, currentPhotoIndex]);

  const handleDislike = useCallback(() => {
    if (!currentItem?.id || exitAnimation) return;
    const dwellMs =
      feedCardShownAtRef.current != null ? Date.now() - feedCardShownAtRef.current : null;
    track('feed_dislike', {
      item_id: currentItem.id,
      interaction: 'swipe',
      dwell_ms: dwellMs,
      photo_index: currentPhotoIndex,
    });
    const atLastCard = currentIndex === items.length - 1;
    const prevLength = items.length;
    setExitAnimation('left');
    performItemAction(currentItem.id, 'dislike').catch(() => {});
    setTimeout(() => {
      if (atLastCard) {
        loadMore(true, { advanceToFirstNewIndex: prevLength, onNoMore: () => setShowEndOfFeed(true) });
      } else {
        goNextCard();
      }
      setExitAnimation(null);
    }, EXIT_ANIMATION_MS);
  }, [currentItem, currentIndex, items.length, goNextCard, loadMore, exitAnimation, currentPhotoIndex]);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const armHorizontalSwipe = useCallback(() => {
    gestureAxisRef.current = 'h';
    swipeArmedRef.current = true;
    setIsSwipeMode(true);
    setInteractionMode('swipe');
    clearHoldTimer();
  }, [clearHoldTimer]);

  const handlePointerCancel = (e) => {
    if (e.pointerId !== activePointerIdRef.current) return;
    activePointerIdRef.current = null;
    if (!isTracking.current) return;
    isTracking.current = false;
    clearHoldTimer();
    setShowEdgeHints(false);
    setRotationClass('');
    setInteractionMode(null);
    setIsSwipeMode(false);
    setCurrentZone('center');
    gestureAxisRef.current = null;
    swipeArmedRef.current = false;
    maxDx.current = 0;
    maxDy.current = 0;
  };

  const handlePointerDown = (e) => {
    if (!e.isPrimary) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (exitAnimation || interactionMode !== null) return;

    activePointerIdRef.current = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }

    isTracking.current = true;
    setIsSwipeMode(false);
    swipeArmedRef.current = false;
    gestureAxisRef.current = null;
    const x = e.clientX;
    const y = e.clientY;
    touchStartX.current = x;
    touchStartY.current = y;
    touchStartTime.current = Date.now();
    maxDx.current = 0;
    maxDy.current = 0;
    gestureContentRectRef.current = e.currentTarget.getBoundingClientRect();
    const zone = getZoneByClientX(x, gestureContentRectRef.current);
    touchZoneRef.current = zone;
    setCurrentZone(zone);
    setShowEdgeHints(false);
    setRotationClass('');

    clearHoldTimer();
    holdTimerRef.current = setTimeout(() => {
      if (
        !isTracking.current ||
        swipeArmedRef.current ||
        gestureAxisRef.current === 'v' ||
        maxDx.current > HOLD_CANCEL_MOVE_PX ||
        maxDy.current > HOLD_CANCEL_MOVE_PX
      ) {
        return;
      }
      setShowEdgeHints(true);
    }, HOLD_HINT_MS);
  };

  const handlePointerMove = (e) => {
    if (!isTracking.current || e.pointerId !== activePointerIdRef.current) return;
    const x = e.clientX;
    const y = e.clientY;
    const dx = x - touchStartX.current;
    const dy = y - touchStartY.current;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    maxDx.current = Math.max(maxDx.current, absDx);
    maxDy.current = Math.max(maxDy.current, absDy);

    if (absDx > HOLD_CANCEL_MOVE_PX || absDy > HOLD_CANCEL_MOVE_PX) {
      setShowEdgeHints(false);
      clearHoldTimer();
    }

    if (gestureAxisRef.current === 'v') {
      return;
    }

    if (gestureAxisRef.current === null && (absDx >= AXIS_LOCK_PX || absDy >= AXIS_LOCK_PX)) {
      if (absDx >= absDy * AXIS_DOMINANCE_RATIO) {
        gestureAxisRef.current = 'h';
      } else if (absDy >= absDx * AXIS_DOMINANCE_RATIO) {
        gestureAxisRef.current = 'v';
        setShowEdgeHints(false);
        clearHoldTimer();
        return;
      }
    }

    const horizontalIntent =
      gestureAxisRef.current === 'h' ||
      (gestureAxisRef.current === null && absDx >= SHORT_SWIPE_COMMIT_PX && absDx > absDy * AXIS_DOMINANCE_RATIO);

    if (horizontalIntent) {
      if (!swipeArmedRef.current) {
        armHorizontalSwipe();
      }
      setRotationClass(dx > 0 ? 'like-active' : dx < 0 ? 'dislike-active' : '');
      setCurrentZone(dx > 0 ? 'right' : dx < 0 ? 'left' : 'center');
    }
  };

  const handlePointerUp = (e) => {
    if (!isTracking.current || e.pointerId !== activePointerIdRef.current) {
      return;
    }
    isTracking.current = false;
    activePointerIdRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    clearHoldTimer();
    setShowEdgeHints(false);

    if (gestureAxisRef.current === 'v') {
      setRotationClass('');
      setInteractionMode(null);
      setIsSwipeMode(false);
      setCurrentZone('center');
      gestureAxisRef.current = null;
      swipeArmedRef.current = false;
      maxDx.current = 0;
      maxDy.current = 0;
      return;
    }

    const endX = e.clientX;
    const finalDx = endX - touchStartX.current;
    const elapsed = Date.now() - touchStartTime.current;
    const maxDxVal = maxDx.current;
    const maxDyVal = maxDy.current;
    const zoneForTap = touchZoneRef.current;

    if (swipeArmedRef.current && Math.abs(finalDx) < SHORT_SWIPE_COMMIT_PX) {
      setRotationClass('');
      setTimeout(() => setInteractionMode(null), 0);
      setIsSwipeMode(false);
      setCurrentZone('center');
      gestureAxisRef.current = null;
      swipeArmedRef.current = false;
      maxDx.current = 0;
      maxDy.current = 0;
      return;
    }

    const horizontalIntentEnd =
      gestureAxisRef.current !== 'v' &&
      maxDxVal >= SHORT_SWIPE_COMMIT_PX &&
      maxDxVal >= maxDyVal * AXIS_DOMINANCE_RATIO;

    const swipeByDirection =
      swipeArmedRef.current ||
      (horizontalIntentEnd && Math.abs(finalDx) >= SHORT_SWIPE_COMMIT_PX);

    if (swipeByDirection && Math.abs(finalDx) >= SHORT_SWIPE_COMMIT_PX) {
      setRotationClass('');
      if (finalDx > 0) handleLike();
      else if (finalDx < 0) handleDislike();
      setTimeout(() => {
        setInteractionMode(null);
        setIsSwipeMode(false);
      }, 100);
      setCurrentZone('center');
      gestureAxisRef.current = null;
      swipeArmedRef.current = false;
      maxDx.current = 0;
      maxDy.current = 0;
      return;
    }

    if (horizontalIntentEnd && Math.abs(finalDx) < SHORT_SWIPE_COMMIT_PX) {
      setRotationClass('');
      setInteractionMode(null);
      setIsSwipeMode(false);
      setCurrentZone('center');
      gestureAxisRef.current = null;
      swipeArmedRef.current = false;
      maxDx.current = 0;
      maxDy.current = 0;
      return;
    }

    const heldForHints = elapsed >= HOLD_HINT_MS;
    const isShortPress = elapsed <= TAP_MAX_DURATION_MS;
    const hasMinimalMovement = maxDxVal <= TAP_MOVE_TOLERANCE_PX && maxDyVal <= TAP_MOVE_TOLERANCE_PX;

    if (!swipeArmedRef.current && gestureAxisRef.current !== 'h') {
      if (heldForHints && hasMinimalMovement) {
        setRotationClass('');
        setTimeout(() => setInteractionMode(null), 0);
      } else if (isShortPress && hasMinimalMovement) {
        e.preventDefault();
        setRotationClass('');
        if (zoneForTap === 'left') prevPhoto();
        else if (zoneForTap === 'right') nextPhoto();
        else openProduct(currentItem);
        setTimeout(() => setInteractionMode(null), 100);
      } else {
        setInteractionMode(null);
      }
    }

    setCurrentZone('center');
    gestureAxisRef.current = null;
    swipeArmedRef.current = false;
    maxDx.current = 0;
    maxDy.current = 0;
  };

  if (error && items.length === 0) {
    return (
      <div className="feed-page">
        <div className="feed-page__content">
          <div className="feed-page__error">{error}</div>
        </div>
      </div>
    );
  }

  if (loading && items.length === 0) {
    return (
      <div className="feed-page">
        <div className="feed-page__content">
          <div className="feed-page__loading">Загрузка ленты…</div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="feed-page">
        <div className="feed-page__content">
          <div className="feed-page__empty">Пока нет товаров в ленте</div>
        </div>
      </div>
    );
  }

  const edgeHintsSwipeLike = isSwipeMode && rotationClass === 'like-active';
  const edgeHintsSwipeDislike = isSwipeMode && rotationClass === 'dislike-active';
  const edgeHintsVisible = showEdgeHints || edgeHintsSwipeLike || edgeHintsSwipeDislike;
  const edgeHintsSwipeClass = edgeHintsSwipeLike
    ? ' feed-page__edge-hints--swipe-like'
    : edgeHintsSwipeDislike
      ? ' feed-page__edge-hints--swipe-dislike'
      : '';

  if (showEndOfFeed) {
    return (
      <div className="feed-page">
        <div className="feed-page__content">
          <div className="feed-page__end">
            <div className="feed-page__end-visual" aria-hidden="true" />
            <h2 className="feed-page__end-title">Лента закончилась</h2>
            <p className="feed-page__end-text">Вы просмотрели все товары. Загляните позже или обновите ленту.</p>
            <button
              type="button"
              className="feed-page__end-btn"
              onClick={() => {
                track('feed_end_refresh_click', {});
                setShowEndOfFeed(false);
                loadMore(false);
              }}
            >
              Обновить ленту
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feed-page">
      <div
        className="feed-page__content feed-page__content--gestures"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onLostPointerCapture={handlePointerCancel}
      >
        <div className="feed-page__cards-stack">
          {stackSlots.map((item, index) => {
            const zIndex = STACK_SIZE - index;
            if (!item) {
              return (
                <div
                  key={`card-${index}`}
                  className="feed-page__card-wrapper"
                  style={{ zIndex, display: 'none' }}
                  aria-hidden="true"
                />
              );
            }
            const isActive = index === 0;
            return (
              <div
                key={`card-${index}`}
                className={`feed-page__card-wrapper ${isActive ? 'feed-page__card-wrapper--active' : ''}`}
                style={{ zIndex, display: 'block' }}
              >
                <FeedCard
                  item={item}
                  rotationClass={isActive && !exitAnimation ? rotationClass : ''}
                  photoIndex={isActive ? currentPhotoIndex : 0}
                  exitAnimation={isActive ? exitAnimation : ''}
                />
              </div>
            );
          })}
        </div>
        <div
          className={`feed-page__edge-hints${edgeHintsVisible ? ' feed-page__edge-hints--visible' : ''}${edgeHintsSwipeClass}`}
          aria-hidden="true"
        >
          <div className="feed-page__edge-hint feed-page__edge-hint--left" />
          <div className="feed-page__edge-hint feed-page__edge-hint--right" />
        </div>
      </div>
    </div>
  );
}
